import despia from "despia-native";
import { isDespiaNative } from "@/lib/isDespiaNative";

export type RGB = { r: number; g: number; b: number };
export type IconColor = "black" | "white";

const DEFAULT_RGB: RGB = { r: 255, g: 255, b: 255 };

function parseColor(input: string | null | undefined): RGB | null {
  if (!input) return null;
  const s = input.trim().toLowerCase();
  if (!s || s === "transparent") return null;

  // rgb / rgba
  const m = s.match(/rgba?\(\s*([\d.]+)\s*[, ]\s*([\d.]+)\s*[, ]\s*([\d.]+)\s*(?:[,/]\s*([\d.]+))?\s*\)/);
  if (m) {
    const a = m[4] !== undefined ? parseFloat(m[4]) : 1;
    if (a === 0) return null;
    return { r: Math.round(+m[1]), g: Math.round(+m[2]), b: Math.round(+m[3]) };
  }

  // hex
  if (s.startsWith("#")) {
    let hex = s.slice(1);
    if (hex.length === 3) hex = hex.split("").map((c) => c + c).join("");
    if (hex.length === 6 || hex.length === 8) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      if (!Number.isNaN(r) && !Number.isNaN(g) && !Number.isNaN(b)) return { r, g, b };
    }
  }
  return null;
}

function parseGradientColor(input: string | null | undefined): RGB | null {
  if (!input || input === "none") return null;
  const matches = input.match(/rgba?\([^)]*\)|#[0-9a-fA-F]{3,8}/g);
  const colors = matches?.map(parseColor).filter(Boolean) as RGB[] | undefined;
  if (!colors?.length) return null;
  if (colors.length === 1) return colors[0];

  // A native status bar is a solid color, so approximate gradients with the
  // midpoint between the first two visible stops (matches our top headers well).
  return {
    r: Math.round((colors[0].r + colors[1].r) / 2),
    g: Math.round((colors[0].g + colors[1].g) / 2),
    b: Math.round((colors[0].b + colors[1].b) / 2),
  };
}

function elementBgColor(el: Element): RGB | null {
  try {
    const cs = window.getComputedStyle(el);
    return parseColor(cs.backgroundColor) ?? parseGradientColor(cs.backgroundImage);
  } catch {
    return null;
  }
}

export function resolvePageTopColor(): RGB {
  if (typeof document === "undefined" || typeof window === "undefined") return DEFAULT_RGB;
  try {
    // Sample a point just below the safe-area / status bar — top-center of the viewport.
    const x = Math.floor(window.innerWidth / 2);
    // Skip the very first pixel (some overlays use 0,0). Aim around safe-area inset (~20-50px).
    const y = 8;
    let el: Element | null = document.elementFromPoint(x, y);
    while (el) {
      const rgb = elementBgColor(el);
      if (rgb) return rgb;
      el = el.parentElement;
    }
    const bodyRgb = document.body ? elementBgColor(document.body) : null;
    if (bodyRgb) return bodyRgb;
    const htmlRgb = document.documentElement ? elementBgColor(document.documentElement) : null;
    if (htmlRgb) return htmlRgb;
  } catch {
    // fall through
  }
  return DEFAULT_RGB;
}

export function pickIconColor({ r, g, b }: RGB): IconColor {
  // Relative luminance (sRGB)
  const toLin = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const L = 0.2126 * toLin(r) + 0.7152 * toLin(g) + 0.0722 * toLin(b);
  return L > 0.5 ? "black" : "white";
}

function rgbToHex({ r, g, b }: RGB): string {
  const h = (v: number) => v.toString(16).padStart(2, "0");
  return `#${h(r)}${h(g)}${h(b)}`;
}

function updateThemeColorMeta(hex: string) {
  try {
    let meta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement | null;
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "theme-color";
      document.head.appendChild(meta);
    }
    if (meta.content !== hex) meta.content = hex;
  } catch {
    // no-op
  }
}

let lastApplied: string | null = null;

export function applyStatusBar(rgb: RGB, iconColor: IconColor) {
  const hex = rgbToHex(rgb);
  const key = `${hex}|${iconColor}`;
  if (key === lastApplied) return;
  lastApplied = key;

  updateThemeColorMeta(hex);

  if (isDespiaNative()) {
    try {
      void despia(`statusbarcolor://{${rgb.r}, ${rgb.g}, ${rgb.b}}`);
    } catch {
      // no-op
    }
    try {
      void despia(`statusbartextcolor://{${iconColor}}`);
    } catch {
      // no-op
    }
    try {
      void despia(`statusbartextcolor://${iconColor}`);
    } catch {
      // no-op
    }
  }
}

export function syncStatusBarNow() {
  const rgb = resolvePageTopColor();
  applyStatusBar(rgb, pickIconColor(rgb));
}
